"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { format } from "date-fns";
import { useLocale } from "next-intl";
import { memo, useCallback, useEffect, useMemo } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { ar, az, de, enUS, ru, tr } from "react-day-picker/locale";
import DayButton from "./DayButton";
import {
  dateFormats,
  formatDate,
  getShortDayNames,
} from "@/utils/dateFormatter";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useIsMobile } from "@/hooks/useIsMobile";

const DatePickerCalendarContent = memo(
  ({
    value,
    handleDateSelect,
    defaultMonth,
    checkinDate,
    checkoutDate,
    publicHolidays,
  }) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector("hotelSearch");
    const defaultClassNames = getDefaultClassNames();

    const defaultMonthValue = useMemo(() => {
      if (isMobile) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth());
      } else {
        const date = new Date(defaultMonth);
        return new Date(date.getFullYear(), date.getMonth());
      }
    }, [isMobile, defaultMonth]);

    const memoizedDayButton = useCallback(
      (props) => (
        <DayButton
          {...props}
          showCheckoutToolTip={!checkoutDate ? t("addCheckoutDate") : false}
          isHoliday={publicHolidays.some(
            (holiday) =>
              holiday.date ===
              formatDate(props.day.date, { format: "YYYY-MM-DD" }),
          )}
        />
      ),
      [publicHolidays, checkoutDate, t],
    );

    // Geçmiş tarih kontrolü ile handleDateSelect'i wrap et
    const handleDateSelectWithValidation = useCallback(
      (date, triggerDate) => {
        // Bugünün başlangıcını al
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Eğer seçilen tarih (from veya to) bugünden önceyse, seçimi engelle
        if (date?.from) {
          const fromDate = new Date(date.from);
          fromDate.setHours(0, 0, 0, 0);
          if (fromDate < today) {
            return; // Geçmiş tarih seçimini engelle
          }
        }

        if (date?.to) {
          const toDate = new Date(date.to);
          toDate.setHours(0, 0, 0, 0);
          if (toDate < today) {
            return; // Geçmiş tarih seçimini engelle
          }
        }

        // Geçerli tarihse, normal handleDateSelect'i çağır
        handleDateSelect(date, triggerDate);
      },
      [handleDateSelect],
    );

    const disabled = useMemo(() => {
      // Bugünün başlangıcını al (saat, dakika, saniye, milisaniye sıfırlanmış)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Her gün için disable kontrolü yapan fonksiyon
      return (date) => {
        const dateToCheck = new Date(date);
        dateToCheck.setHours(0, 0, 0, 0);

        // Sadece geçmiş tarihleri disable et
        // (Bugünden önceki günler seçilemez, fakat bugünden sonraki tüm günler seçilebilir)
        if (dateToCheck < today) {
          return true;
        }

        // Gelecek tarihlerin tamamı seçilebilir - checkin'den önceki günler de dahil.
        // Giriş/çıkış mantığı DateRangePicker içindeki onSelect (onSelectCheckoutDate)
        // fonksiyonunda yönetiliyor.
        return false;
      };
    }, []);

    useEffect(() => {
      setTimeout(() => {
        const container = document.querySelector(".months-container");
        const selectedMonth = container?.querySelector(".selected-month");

        if (container && selectedMonth) {
          const selectedTop = selectedMonth.offsetTop;
          const containerHeight = container.clientHeight;
          const selectedHeight = selectedMonth.clientHeight;

          // Seçili elemanı container'ın ortasına getir
          const scrollTo =
            selectedTop - containerHeight / 2 + selectedHeight / 2;

          container.scrollTop = scrollTo;
        }
      }, 100);
    }, []);

    return (
      <div className="flex h-full flex-col gap-4 px-2 py-4 md:px-4">
        <div className="flex items-center justify-between gap-4 px-2 md:hidden md:px-0">
          <button
            // onClick={mobileCheckinClick}
            className={clsx(
              "bg-primary-weaker flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-1 px-3 py-2",
              !checkinDate
                ? "border-primary bg-primary-weaker border-2"
                : "border-border bg-white",
            )}
          >
            <span className="text-xs">{t("checkin")}</span>
            <span className="text-sm font-medium">
              {dateFormats.dayLongMonthShortDay(checkinDate, locale)}
            </span>
          </button>
          <button
            // onClick={mobileCheckoutClick}
            className={clsx(
              "flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-1 px-3 py-2",
              !checkoutDate
                ? "border-primary bg-primary-weaker border-2"
                : "border-border bg-white",
            )}
          >
            <span className="text-xs">{t("checkout")}</span>
            <span className="text-sm font-medium">
              {checkoutDate
                ? dateFormats.dayLongMonthShortDay(checkoutDate, locale)
                : ""}
            </span>
          </button>
        </div>

        <div className="border-primary-weak flex items-center justify-between border-b md:hidden">
          {getShortDayNames(locale).map((day) => (
            <span
              className="text-foreground-muted h-11 w-[55px] py-2 text-center text-sm font-medium"
              key={day}
            >
              {day}
            </span>
          ))}
        </div>

        <div className="months-container h-full overflow-scroll px-0 pb-4 md:overflow-visible md:pb-0">
          <DayPicker
            key={`daypicker-${isMobile ? "mobile" : "desktop"}`}
            formatters={{
              formatWeekdayName: (date, options) =>
                format(date, "ccc", options),
            }}
            locale={
              locale === "tr"
                ? tr
                : locale === "ar"
                  ? ar
                  : locale === "de"
                    ? de
                    : locale === "en"
                      ? enUS
                      : locale === "ru"
                        ? ru
                        : locale === "az"
                          ? az
                          : tr
            }
            animate
            mode="range"
            selected={value}
            onSelect={handleDateSelectWithValidation}
            // onDayMouseEnter={!isMobile ? handleDayMouseEnter : undefined}
            // onDayMouseLeave={!isMobile ? handleDayMouseLeave : undefined}
            // modifiers={!isMobile ? modifiers : {}}
            // modifiersStyles={{
            //   hoverRange: {
            //     backgroundColor: "#F2F2F9",
            //   },
            // }}
            numberOfMonths={isMobile ? 12 : 2}
            navLayout="around"
            hideWeekdays={isMobile ? true : false}
            // hideNavigation={isMobile ? true : false}
            defaultMonth={defaultMonthValue}
            disabled={disabled}
            classNames={{
              root: `${defaultClassNames.root} [&>*]:select-none`,
              months: `${defaultClassNames.months} justify-between !max-w-full`,
              month: `${defaultClassNames.month} w-full md:w-auto`,
              month_grid: `${defaultClassNames.month_grid} w-full`,
              caption_label: `${defaultClassNames.caption_label} font-semibold`,
              disabled: "text-foreground-weaker-subtext",
              today: "text-primary",
              day: "font-semibold w-[55px] h-[48px] md:h-[58px]",
              selected: "",
              weekday:
                "border-b border-primary-weak text-sm text-foreground-muted font-medium h-11",
              button_next:
                "absolute right-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
              button_previous:
                "absolute left-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
            }}
            components={{
              DayButton: memoizedDayButton,
              Month: (props) => {
                const currentMonthHolidays = publicHolidays.filter(
                  (holiday) => {
                    const holidayDate = new Date(holiday.date);
                    const calendarDate = props.calendarMonth.date;

                    return (
                      holidayDate.getFullYear() ===
                        calendarDate.getFullYear() &&
                      holidayDate.getMonth() === calendarDate.getMonth()
                    );
                  },
                );

                // Aynı başlığa sahip ardışık günleri aralıklara grupla, sonra çakışan aralıkları birleştir
                const groupedHolidays = (() => {
                  if (!currentMonthHolidays.length) return [];

                  const sorted = [...currentMonthHolidays].sort(
                    (a, b) =>
                      new Date(a.date).getTime() - new Date(b.date).getTime(),
                  );

                  const groups = [];
                  let current = null;

                  sorted.forEach((holiday) => {
                    const date = new Date(holiday.date);
                    date.setHours(0, 0, 0, 0);

                    if (!current) {
                      current = {
                        start: date,
                        end: date,
                        title: holiday.title,
                      };
                      return;
                    }

                    const nextDay = new Date(current.end);
                    nextDay.setDate(nextDay.getDate() + 1);

                    if (
                      holiday.title === current.title &&
                      date.getTime() === nextDay.getTime()
                    ) {
                      current.end = date;
                    } else {
                      groups.push(current);
                      current = {
                        start: date,
                        end: date,
                        title: holiday.title,
                      };
                    }
                  });

                  if (current) {
                    groups.push(current);
                  }

                  // Çakışan veya bitişik (ardışık) aralıkları tek satırda birleştir
                  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
                  const merged = [];
                  const sortedGroups = [...groups].sort(
                    (a, b) => a.start.getTime() - b.start.getTime(),
                  );

                  const overlapsOrAdjacent = (m, gStart, gEnd) => {
                    const mStart = m.start.getTime();
                    const mEnd = m.end.getTime();
                    const overlap = mStart <= gEnd && gStart <= mEnd;
                    const adjacent =
                      mEnd + ONE_DAY_MS === gStart ||
                      gEnd + ONE_DAY_MS === mStart;
                    return overlap || adjacent;
                  };

                  // Arife günlerini bayram aralığıyla birleştirmemek için başlık kontrolü
                  const isArife = (titleText) =>
                    titleText && /arife/i.test(titleText);
                  const canMergeTitles = (title1, title2) => {
                    if (!title1 || !title2) return true;
                    // Biri arife, diğeri normal bayram ise ayrı satırda kalsın
                    if (isArife(title1) !== isArife(title2)) return false;
                    return true;
                  };

                  sortedGroups.forEach((g) => {
                    const gStart = g.start.getTime();
                    const gEnd = g.end.getTime();

                    const idx = merged.findIndex(
                      (m) =>
                        canMergeTitles(m.title, g.title) &&
                        overlapsOrAdjacent(m, gStart, gEnd),
                    );

                    if (idx === -1) {
                      merged.push({
                        start: new Date(g.start),
                        end: new Date(g.end),
                        title: g.title,
                      });
                    } else {
                      const m = merged[idx];
                      m.start = new Date(Math.min(m.start.getTime(), gStart));
                      m.end = new Date(Math.max(m.end.getTime(), gEnd));
                      const hasMeaningful = (titleText) =>
                        titleText && /bayram|tatil|resmi/i.test(titleText);
                      if (hasMeaningful(g.title) && !hasMeaningful(m.title)) {
                        m.title = g.title;
                      }
                    }
                  });

                  // Aynı tarih aralığını kapsayan (içinde olan) satırları kaldır: sadece en geniş aralığı bırak
                  const filtered = merged.filter((outer, i) => {
                    const outerStart = outer.start.getTime();
                    const outerEnd = outer.end.getTime();
                    const isContained = merged.some(
                      (inner, j) =>
                        i !== j &&
                        inner.start.getTime() <= outerStart &&
                        outerEnd <= inner.end.getTime() &&
                        (inner.start.getTime() < outerStart ||
                          outerEnd < inner.end.getTime()),
                    );
                    return !isContained;
                  });

                  return filtered.sort(
                    (a, b) => a.start.getTime() - b.start.getTime(),
                  );
                })();

                return (
                  <div
                    className={clsx(
                      "flex w-full flex-col items-center justify-between gap-2 md:w-auto",
                      props.calendarMonth.date.getFullYear() ===
                        checkinDate.getFullYear() &&
                        props.calendarMonth.date.getMonth() ===
                          checkinDate.getMonth() &&
                        "selected-month",
                    )}
                  >
                    <div className="w-full">{props.children}</div>
                    <div className="flex w-full flex-col items-start gap-1 pl-[14px]">
                      {groupedHolidays.map((group, index) => {
                        const isRange =
                          group.start.getTime() !== group.end.getTime();

                        // Tarih label'ları ve başlığı, tekrar eden tarih bilgisini temizleyerek hazırla
                        let title = group.title || "";
                        let displayStart;
                        let displayEnd = null;

                        if (isRange) {
                          // Örnek: 27 May - 30 May (gün adı yok, ay kısaltması)
                          const shortStart = dateFormats.dayShortMonth(
                            group.start,
                            locale,
                          );
                          const shortEnd = dateFormats.dayShortMonth(
                            group.end,
                            locale,
                          );

                          let cleanTitle = title;
                          if (cleanTitle) {
                            // Başta kalan tüm tarih ifadelerini kaldır (DD Ay, DD Mayıs, " - DD Ay" vb. duplike kalmasın)
                            const dateRangePattern =
                              /^[\s\-–]*\d{1,2}\s+\S+(\s*-\s*\d{1,2}\s+\S+)?\s*/;
                            let prev = "";
                            while (prev !== cleanTitle) {
                              prev = cleanTitle;
                              cleanTitle = cleanTitle
                                .replace(dateRangePattern, "")
                                .trim();
                              // Başta sadece tire/boşluk kaldıysa temizle ki sonraki döngüde "30 Mayıs" yakalansın
                              while (
                                cleanTitle.startsWith("-") ||
                                cleanTitle.startsWith("–") ||
                                /^\s/.test(cleanTitle)
                              ) {
                                cleanTitle = cleanTitle
                                  .replace(/^[\s\-–]+/, "")
                                  .trim();
                              }
                            }
                          }

                          displayStart = shortStart;
                          displayEnd = shortEnd;
                          title = cleanTitle;
                        } else {
                          // Tek gün: 01 May İşçi Bayramı (gün adı yok, ay kısaltması)
                          displayStart = dateFormats.dayShortMonth(
                            group.start,
                            locale,
                          );
                        }

                        return (
                          <div
                            className="flex w-full items-center justify-start gap-[9px]"
                            key={`${group.title}-${group.start.toISOString()}-${index}`}
                          >
                            <span className="bg-tertiary-emphasis h-[6px] w-[6px] rounded-full"></span>
                            <span className="text-foreground-muted inline-flex items-center gap-1 text-sm">
                              <span>
                                {isRange
                                  ? `${displayStart} - ${displayEnd}`
                                  : displayStart}
                              </span>
                              {title && <span>{title}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              },
              NextMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-right"]}
                    {...props}
                  />
                );
              },
              PreviousMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-left"]}
                    {...props}
                  />
                );
              },
            }}
          />
        </div>
      </div>
    );
  },
);

DatePickerCalendarContent.displayName = "DatePickerCalendarContent";

export default DatePickerCalendarContent;
